import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef } from 'react'
import { reaction } from 'mobx'
import { Button, Checkbox, Input, message, Modal, Segmented, Select, Skeleton, Space, Table, Tooltip, TreeSelect } from 'antd'
import type { ColumnsType, SortOrder } from 'antd/es/table/interface'
import {
    AppstoreOutlined,
    ApartmentOutlined,
    ArrowDownOutlined,
    ArrowUpOutlined,
    BarcodeOutlined,
    CheckOutlined,
    CloseOutlined,
    DownOutlined,
    DeleteOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    ExportOutlined,
    PlusOutlined,
    RightOutlined,
    SearchOutlined,
    ShoppingOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
    StarFilled,
    VerticalAlignTopOutlined,
} from '@ant-design/icons'
import i from '../../translations/i'
import { WarehousesController } from '../../front/settings/warehouses/warehouses.controller'
import { storeLocal } from '../../helpers/stores'
import { getPrice } from '../../helpers/price'
import { CardList, cleanText, ListCard, ListEmpty, ListToolbar, ListToolbarField, ListToolbarSpacer, useNarrowScreen } from '../card_list/CardList'
import { VisibilityToggle } from '../visibility_toggle/VisibilityToggle'
import {
    PRODUCT_SORT_KEYS,
    ProductSearchField,
    ProductSortKey,
    ProductStatus,
    ProductTableController,
    WITHOUT_CATEGORY,
} from './product_table.controller'
import './product_table.less'

// Единая таблица товаров админки: общий список, товары категории в каталоге, товары без категории,
// выбор товаров в модалках (picker). Состояние — в ProductTableController, у каждого экрана свой экземпляр.

// Режим выбора: вместо перехода в товар — «Выбрать», без переключателя показа, массовых действий и порядка
export interface ProductPickerMode {
    selectedIds: number[]
    onToggle: (product: any, selected: boolean) => void
    // Добавление вместо выбора (товар в заказ): «Добавить», у добавленного — «Добавлен», убрать его здесь нельзя
    add?: boolean
    // Что можно выбрать: у остальных нет кнопки, а клик по строке раскрывает вариации
    canPick?: (product: any) => boolean
    // Своё содержимое под названием товара — например, выбор свойства перед добавлением
    renderExtra?: (product: any) => React.ReactNode
}

const canPick = (picker: ProductPickerMode, product: any) => picker.canPick?.(product) ?? true

const Warehouses = new WarehousesController()

const productUrl = (id: number) => `/shop/catalog/product/${id}`

// Продавцу (4) склады не показываем, продавец и франчайзи (6) не меняют порядок в каталоге
const role = () => Number(storeLocal.get('role'))

// Строки-заглушки, пока не пришла первая страница: шапка таблицы видна сразу
const SKELETON_ROWS = [-1, -2, -3, -4, -5].map(id => ({ id, __skeleton: true }))

export const productImage = (image: any): string | null => {
    if (!image) return null
    if (image.path) return image.path
    return image.uri ? `${image.base ?? ''}${image.uri}` : null
}

// Свойства вариации: params каталога ({ name, values: [{ value }] }) или props из /products/select ({ category: { name }, value })
const variantParams = (product: any): { name: string, value: any }[] => {
    if (Array.isArray(product.params)) {
        return product.params
            .filter((param: any) => param?.values?.[0]?.value !== undefined)
            .map((param: any) => ({ name: param.name, value: param.values[0].value }))
    }
    if (product.is_variant && Array.isArray(product.props)) {
        return product.props
            .filter((prop: any) => prop?.value !== undefined && prop?.value !== null)
            .map((prop: any) => ({ name: prop.category?.name, value: prop.value }))
    }
    return []
}

const copy = (value: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard?.writeText(value).then(() => message.success(i.t('list:copied')))
}

// Значение, которое копируется по клику: ID, артикул, ID в CRM
const CopyMeta = ({ value, hint, icon, children }: { value: string, hint: string, icon?: React.ReactNode, children: React.ReactNode }) => (
    <Tooltip title={hint} mouseEnterDelay={0.4}>
        <span className="pt-copy" onClick={copy(value)}>{icon}{children}</span>
    </Tooltip>
)

// Показ товара в каталоге — слева от картинки, без подписи: состояние видно по цвету и приглушённой строке
const CatalogToggle = observer(({ table, product }: { table: ProductTableController, product: any }) => {
    const visible = Number(product.view) > 0

    return (
        <VisibilityToggle
            minimal
            checked={visible}
            loading={table.isBusy(product.id)}
            onLabel={i.t('productCards:inCatalog')}
            offLabel={i.t('productCards:hidden')}
            hint={visible ? i.t('productCards:toggleOnHint') : i.t('productCards:toggleOffHint')}
            onChange={checked => table.setActive(product.id, checked)}
        />
    )
})

// Вариации товара раскрываются вложенными строками
const VariationsTag = observer(({ table, product }: { table: ProductTableController, product: any }) => {
    const count = Array.isArray(product.children) ? product.children.length : 0
    if (!count) return <span className="pt-tag pt-tag--primary"><ApartmentOutlined />{i.t('productCards:variations')}</span>

    const expanded = table.expandedIds.includes(product.id)

    return (
        <button
            type="button"
            className={`pt-tag pt-tag--primary pt-tag--action${expanded ? ' pt-tag--open' : ''}`}
            aria-expanded={expanded}
            onClick={() => table.toggleExpanded(product.id)}
        >
            <ApartmentOutlined />
            {i.t('productCards:variationsCount', { count })}
            <DownOutlined className="pt-tag__chevron" />
        </button>
    )
})

// ID, артикул, CRM и метки товара: под названием в строке таблицы и в карточке
const ProductMeta = ({ table, product, picker }: { table: ProductTableController, product: any, picker?: ProductPickerMode }) => {
    const vendorCode = cleanText(product.vendor_code)
    const idCrm = cleanText(product.id_crm)
    const params = variantParams(product)

    return (
        <div className="pt-product__meta">
            <CopyMeta value={`${product.id}`} hint={i.t('productCards:idHint')}>#{product.id}</CopyMeta>
            {picker && !(Number(product.view) > 0) && <span className="pt-tag"><EyeInvisibleOutlined />{i.t('productCards:hidden')}</span>}
            {vendorCode && <CopyMeta value={vendorCode} hint={i.t('productCards:vendorHint')} icon={<BarcodeOutlined />}>{vendorCode}</CopyMeta>}
            {idCrm && <CopyMeta value={idCrm} hint={i.t('productCards:crmHint')}>CRM {idCrm}</CopyMeta>}
            {!!product.is_set && <span className="pt-tag pt-tag--info"><AppstoreOutlined />{i.t('productCards:set')}</span>}
            {!!product.view_variations && <VariationsTag table={table} product={product} />}
            {product.product_id > 0 && !params.length && <span className="pt-tag"><ApartmentOutlined />{i.t('productCards:variant')}</span>}
            {params.map((param, index) =>
                <span key={index} className="pt-tag">{param.name}: {param.value}</span>
            )}
        </div>
    )
}

const ProductThumb = ({ product }: { product: any }) => {
    const image = productImage(product.image)

    return (
        <span className={`pt-thumb${image ? '' : ' pt-thumb--empty'}`}>
            {image ? <img src={image} alt="" loading="lazy" /> : <ShoppingOutlined />}
        </span>
    )
}

const ProductCell = observer(({ table, product, picker }: { table: ProductTableController, product: any, picker?: ProductPickerMode }) => {
    const name = cleanText(product.name)
    const extra = picker?.renderExtra?.(product)

    return (
        <div className="pt-product">
            {!picker && <CatalogToggle table={table} product={product} />}
            <ProductThumb product={product} />
            <div className="pt-product__body">
                {picker
                    ? <span className="pt-product__name" title={name}>{name || i.t('productCards:noName')}</span>
                    : <a className="pt-product__name" href={productUrl(product.id)} title={name}>
                        {name || i.t('productCards:noName')}
                    </a>
                }
                <ProductMeta table={table} product={product} picker={picker} />
                {extra && <div className="pt-product__extra">{extra}</div>}
            </div>
        </div>
    )
})

// Скидка в процентах, если старая цена выше текущей
const discountPercent = (product: any): number => {
    const price = Number(product.price ?? 0)
    const oldPrice = Number(product.old_price ?? 0)

    return oldPrice > price && price > 0 ? Math.round((1 - price / oldPrice) * 100) : 0
}

const PriceCell = ({ product }: { product: any }) => {
    const price = Number(product.price ?? 0)
    const oldPrice = Number(product.old_price ?? 0)
    const discount = discountPercent(product)
    const unit = cleanText(product.unit?.name)

    return (
        <div className="pt-price">
            <span className="pt-price__now">{getPrice(price)}</span>
            {discount > 0
                ? <Tooltip title={i.t('productCards:oldPriceHint', { price: getPrice(oldPrice) })}>
                    <span className="pt-price__sub">
                        <s>{getPrice(oldPrice)}</s>
                        <span className="pt-pill pt-pill--success">−{discount}%</span>
                    </span>
                </Tooltip>
                : !!product.weight_goods && unit && <span className="pt-price__sub">{i.t('productCards:per', { unit })}</span>
            }
        </div>
    )
}

// Остаток не ведётся — серым; закончился — красным
const StockCell = ({ product }: { product: any }) => {
    if (product.count === null || product.count === undefined || product.count === '') {
        return (
            <Tooltip title={i.t('productCards:stockUnknownHint')}>
                <span className="pt-stock pt-stock--unknown"><i />{i.t('productCards:stockUnknown')}</span>
            </Tooltip>
        )
    }

    const count = Number(product.count)
    const unit = cleanText(product.unit?.name)

    return count > 0
        ? <span className="pt-stock pt-stock--ok"><i />{count.toLocaleString()}{unit && <small>{unit}</small>}</span>
        : <span className="pt-stock pt-stock--out"><i />{i.t('productCards:stockOut')}</span>
}

const RatingCell = ({ product }: { product: any }) => {
    const rating = product.rating?.ratingCount > 0 ? product.rating : null
    if (!rating) return (
        <Tooltip title={i.t('productCards:noRating')}>
            <span className="pt-muted">—</span>
        </Tooltip>
    )

    const value = Number(rating.ratingAvg).toFixed(1)

    return (
        <Tooltip title={i.t('productCards:ratingHint', { rating: value, count: rating.ratingCount })}>
            <span className="pt-rating"><StarFilled />{value}<small>{rating.ratingCount.toLocaleString()}</small></span>
        </Tooltip>
    )
}

// Порядок товара в категории — как его видят покупатели в каталоге
const MoveButtons = observer(({ table, product }: { table: ProductTableController, product: any }) => {
    const disabled = table.isBusy(product.id) || [4, 6].includes(role())

    return (
        <span className="pt-move">
            <Tooltip title={i.t('productCards:moveTop')}>
                <Button type="text" size="small" icon={<VerticalAlignTopOutlined />} disabled={disabled} onClick={() => table.move(product.id, 'all_up')} />
            </Tooltip>
            <Tooltip title={i.t('productCards:moveUp')}>
                <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={disabled} onClick={() => table.move(product.id, 'up')} />
            </Tooltip>
            <Tooltip title={i.t('productCards:moveDown')}>
                <Button type="text" size="small" icon={<ArrowDownOutlined />} disabled={disabled} onClick={() => table.move(product.id, 'down')} />
            </Tooltip>
        </span>
    )
})

// «Выбрать» / «Выбран» в режиме выбора («Добавить» / «Добавлен» — в режиме добавления);
// рядом — открыть товар в новой вкладке, не закрывая модалку
const PickActions = ({ product, picker }: { product: any, picker: ProductPickerMode }) => {
    const selected = picker.selectedIds.includes(Number(product.id))

    return (
        <span className="pt-actions">
            <Tooltip title={i.t('productCards:openNewTab')}>
                <Button className="pt-open" type="text" shape="circle" icon={<ExportOutlined />} href={productUrl(product.id)} target="_blank" rel="noreferrer" />
            </Tooltip>
            {canPick(picker, product) && (picker.add
                ? <Button
                    className={`pt-pick${selected ? ' pt-pick--done' : ''}`}
                    icon={selected ? <CheckOutlined /> : <PlusOutlined />}
                    disabled={selected}
                    onClick={() => picker.onToggle(product, true)}
                >
                    {selected ? i.t('productCards:added') : i.t('productCards:add')}
                </Button>
                : <Button
                    className="pt-pick"
                    type={selected ? 'primary' : 'default'}
                    icon={selected ? <CheckOutlined /> : <PlusOutlined />}
                    onClick={() => picker.onToggle(product, !selected)}
                >
                    {selected ? i.t('productCards:picked') : i.t('productCards:pick')}
                </Button>
            )}
        </span>
    )
}

// Компактная панель: одна строка поиска с выбором поля, категория, склад и статус
const ProductToolbar = observer(({ table }: { table: ProductTableController }) => {
    const { categoryId, searchField } = table
    const categoryValue = categoryId === null
        ? undefined
        : categoryId === WITHOUT_CATEGORY ? WITHOUT_CATEGORY : Number(categoryId)

    const allowedWarehouseIds: number[] = storeLocal.get('warehouses_access') || []
    const warehouses = role() === 6
        ? Warehouses.list.filter((el: any) => allowedWarehouseIds.includes(el.id))
        : Warehouses.list

    return (
        <ListToolbar>
            <ListToolbarField wide>
                <Space.Compact block className="pt-search">
                    <Select
                        className="pt-search__by"
                        value={searchField}
                        popupMatchSelectWidth={false}
                        options={table.searchFields.map(field => ({ value: field, label: i.t(`productCards:by.${field}`) }))}
                        onChange={(field: ProductSearchField) => table.setSearchField(field)}
                    />
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder={i.t(`productCards:placeholder.${searchField}`)}
                        value={table.searchText}
                        onChange={e => table.setSearch(e.target.value)}
                    />
                </Space.Compact>
            </ListToolbarField>

            {table.hasFilters && !table.fixedCategory &&
                <ListToolbarField>
                    <TreeSelect
                        allowClear
                        showSearch
                        treeNodeFilterProp="title"
                        popupMatchSelectWidth={false}
                        style={{ width: '100%' }}
                        placeholder={i.t('productCards:allCategories')}
                        value={categoryValue}
                        treeData={[
                            { value: WITHOUT_CATEGORY, title: i.t('productCards:withoutCategory') },
                            ...table.categoryTree,
                        ]}
                        onChange={(value: any) => table.setCategory(value)}
                    />
                </ListToolbarField>
            }

            {table.hasFilters && role() !== 4 &&
                <ListToolbarField>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={i.t('productCards:allWarehouses')}
                        value={table.warehouseId ?? undefined}
                        options={warehouses.map((el: any) => ({ label: el.name, value: el.id }))}
                        onChange={value => table.setWarehouse(value ?? null)}
                    />
                </ListToolbarField>
            }

            <ListToolbarSpacer />

            {table.hasFilters &&
                <Segmented
                    value={table.status}
                    options={[
                        { label: i.t('productCards:statusAll'), value: 'all' },
                        { label: i.t('productCards:statusActive'), value: 'active' },
                        { label: i.t('productCards:statusHidden'), value: 'hidden' },
                    ]}
                    onChange={value => table.setStatus(value as ProductStatus)}
                />
            }

            {table.filtered &&
                <Button type="link" className="pt-reset" icon={<CloseOutlined />} onClick={() => table.resetFilters()}>
                    {i.t('productCards:reset')}
                </Button>
            }
        </ListToolbar>
    )
})

// Полоса массовых действий: накрывает панель фильтров, когда выбраны строки
const BulkBar = observer(({ table }: { table: ProductTableController }) => {
    const count = table.selectedIds.length
    if (count === 0) return null

    const confirmDelete = () => Modal.confirm({
        title: i.t('productCards:bulkDeleteConfirm', { count }),
        content: i.t('productCards:bulkDeleteText'),
        okText: i.t('productCards:bulkDelete'),
        okButtonProps: { danger: true },
        cancelText: i.t('def:no'),
        onOk: () => table.deleteSelected(),
    })

    return (
        <div className="pt-bulk">
            <span className="pt-bulk__count">{i.t('productCards:selected', { count })}</span>
            <div className="pt-bulk__actions">
                <Button size="small" icon={<EyeOutlined />} loading={table.bulkLoading} onClick={() => table.setActiveSelected(true)}>
                    {i.t('productCards:bulkShow')}
                </Button>
                <Button size="small" icon={<EyeInvisibleOutlined />} loading={table.bulkLoading} onClick={() => table.setActiveSelected(false)}>
                    {i.t('productCards:bulkHide')}
                </Button>
                <Button size="small" danger icon={<DeleteOutlined />} disabled={table.bulkLoading} onClick={confirmDelete}>
                    {i.t('productCards:bulkDelete')}
                </Button>
            </div>
            <Button size="small" type="text" className="pt-bulk__clear" icon={<CloseOutlined />} onClick={() => table.setSelectedIds([])}>
                {i.t('productCards:bulkClear')}
            </Button>
        </div>
    )
})

// Клик по товару в режиме выбора: выбирает его, добавленный второй раз не добавляет,
// а у товара, который выбрать нельзя, раскрывает вариации
const pickByClick = (table: ProductTableController, product: any, picker: ProductPickerMode) => {
    const selected = picker.selectedIds.includes(Number(product.id))
    if (!canPick(picker, product)) {
        if (Array.isArray(product.children) && product.children.length) table.toggleExpanded(product.id)
    } else if (!(picker.add && selected)) {
        picker.onToggle(product, !selected)
    }
}

// Клик по строке открывает товар (в режиме выбора — pickByClick); у ссылок, кнопок, полей, переключателя,
// отметки, копируемых значений и своего содержимого строки своё действие
const onRowClick = (table: ProductTableController, product: any, picker?: ProductPickerMode) => (e: React.MouseEvent<HTMLElement>) => {
    if (product.__skeleton) return
    const target = e.target as HTMLElement
    if (!e.currentTarget.contains(target)) return
    if (target.closest('a, button, input, label, [role="switch"], .ant-select, .pt-copy, .pt-product__extra, .ant-checkbox-wrapper, .ant-table-selection-column')) return
    if (window.getSelection()?.toString()) return

    if (picker) pickByClick(table, product, picker)
    else if (e.metaKey || e.ctrlKey) window.open(productUrl(product.id), '_blank')
    else window.location.href = productUrl(product.id)
}

// ---------- Карточки (узкий экран) ----------

const SORT_LABELS: Record<ProductSortKey, string> = {
    name: 'productCards:by.name',
    price: 'productCards:col.price',
    count: 'productCards:col.stock',
}

// Сортировка вместо заголовков колонок: поле и направление
const ProductSort = observer(({ table }: { table: ProductTableController }) => {
    const { sort } = table
    const desc = sort?.order === 'desc'

    return (
        <Space.Compact>
            <Select
                size="small"
                popupMatchSelectWidth={false}
                value={sort?.key ?? 'default'}
                options={[
                    { value: 'default', label: i.t('productCards:sortDefault') },
                    ...PRODUCT_SORT_KEYS.map(key => ({ value: key, label: i.t(SORT_LABELS[key]) })),
                ]}
                onChange={(key: ProductSortKey | 'default') => table.setTableState(1, table.limit, key === 'default' ? null : { key, order: sort?.order ?? 'asc' })}
            />
            {sort &&
                <Tooltip title={i.t(desc ? 'productCards:sortDesc' : 'productCards:sortAsc')}>
                    <Button
                        size="small"
                        aria-label={i.t(desc ? 'productCards:sortDesc' : 'productCards:sortAsc')}
                        icon={desc ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
                        onClick={() => table.setTableState(1, table.limit, { key: sort.key, order: desc ? 'asc' : 'desc' })}
                    />
                </Tooltip>
            }
        </Space.Compact>
    )
})

// Отметить всю страницу для массовых действий — вместо отметки в шапке таблицы
const SelectPage = observer(({ table }: { table: ProductTableController }) => {
    const ids = table.list.map((product: any) => product.id)
    const selected = ids.filter((id: number) => table.selectedIds.includes(id)).length

    return (
        <Checkbox
            checked={selected > 0 && selected === ids.length}
            indeterminate={selected > 0 && selected < ids.length}
            onChange={e => table.setSelectedIds(e.target.checked ? ids : [])}
        >
            {i.t('productCards:selectPage')}
        </Checkbox>
    )
})

// Товар на узком экране — то же, что строка таблицы: цена, остаток и рейтинг уходят в метрики, переключатель и порядок — в действия
const ProductCard = observer(({ table, product, picker }: { table: ProductTableController, product: any, picker?: ProductPickerMode }) => {
    const name = cleanText(product.name)
    const extra = picker?.renderExtra?.(product)
    const selected = picker
        ? picker.selectedIds.includes(Number(product.id))
        : table.selectedIds.includes(product.id)

    const oldPrice = Number(product.old_price ?? 0)
    const discount = discountPercent(product)
    const unit = cleanText(product.unit?.name)

    const toggleSelected = (checked: boolean) => table.setSelectedIds(checked
        ? [...table.selectedIds, product.id]
        : table.selectedIds.filter(id => id !== product.id)
    )

    return (
        <ListCard
            media={<ProductThumb product={product} />}
            title={name || i.t('productCards:noName')}
            titleHint={name}
            href={picker ? undefined : productUrl(product.id)}
            onOpen={picker ? () => pickByClick(table, product, picker) : undefined}
            muted={!picker && !(Number(product.view) > 0)}
            selected={selected}
            chips={<ProductMeta table={table} product={product} picker={picker} />}
            text={extra && <div className="pt-product__extra">{extra}</div>}
            metrics={[
                {
                    value: getPrice(Number(product.price ?? 0)),
                    label: discount > 0
                        ? <><s>{getPrice(oldPrice)}</s> −{discount}%</>
                        : !!product.weight_goods && unit ? i.t('productCards:per', { unit }) : i.t('productCards:col.price'),
                    tone: discount > 0 ? 'success' : undefined,
                },
                { value: <StockCell product={product} />, label: i.t('productCards:col.stock') },
                ...(picker ? [] : [{ value: <RatingCell product={product} />, label: i.t('productCards:col.rating') }]),
            ]}
            actions={picker
                ? <PickActions product={product} picker={picker} />
                : <>
                    <CatalogToggle table={table} product={product} />
                    {table.canMove && <MoveButtons table={table} product={product} />}
                    <Checkbox checked={selected} onChange={e => toggleSelected(e.target.checked)} />
                </>
            }
        />
    )
})

// Товар и, если раскрыты, его вариации под ним
const ProductCardGroup = observer(({ table, product, picker }: { table: ProductTableController, product: any, picker?: ProductPickerMode }) => {
    const children = table.expandedIds.includes(product.id) && Array.isArray(product.children) ? product.children : []

    return <>
        <ProductCard table={table} product={product} picker={picker} />
        {children.length > 0 &&
            <div className="pt-cards-children">
                {children.map((child: any) => <ProductCard key={child.id} table={table} product={child} picker={picker} />)}
            </div>
        }
    </>
})

export const ProductTable = observer(({ table, createHref = '/shop/catalog/product/0', picker }: {
    table: ProductTableController
    // Куда ведёт «Создать товар» в пустом списке — в каталоге сразу с категорией
    createHref?: string
    picker?: ProductPickerMode
}) => {

    const sectionRef = useRef<HTMLElement>(null)
    const narrow = useNarrowScreen()

    useEffect(() => {
        if (table.hasFilters && role() !== 4) Warehouses.get(1000000)
        table.load()

        if (!table.options.syncUrl) return
        return reaction(
            () => table.url,
            url => window.history.replaceState(null, '', url),
        )
    }, [table])

    const { list, loading } = table
    const firstLoad = loading && list.length === 0
    const skeleton = (node: React.ReactNode) => (row: any) => row.__skeleton ? <Skeleton.Input active size="small" block /> : node

    // В модалке листаем к началу списка, на странице — к началу страницы
    const scrollToTop = () => picker
        ? sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
        : window.scrollTo({ top: 0, behavior: 'smooth' })

    const empty = table.filtered
        ? {
            icon: <SearchOutlined />,
            title: i.t('productCards:empty'),
            text: i.t('productCards:emptyHint'),
            action: <Button onClick={() => table.resetFilters()}>{i.t('productCards:reset')}</Button>,
        }
        : {
            icon: <ShoppingOutlined />,
            title: i.t('productCards:emptyAll'),
            text: picker ? undefined : i.t('productCards:emptyAllHint'),
            action: picker ? undefined : <Button type="primary" icon={<PlusOutlined />} href={createHref}>{i.t('categories:createProduct')}</Button>,
        }

    if (narrow) return (
        <section className={`pt pt--cards${picker ? ' pt--picker' : ''}`} ref={sectionRef}>
            <div className="pt__top">
                <ProductToolbar table={table} />
                {!picker && <BulkBar table={table} />}
            </div>

            <CardList
                items={list}
                count={table.total}
                loading={loading}
                extra={list.length > 0 && (table.sortable || !picker) &&
                    <div className="pt-cards-extra">
                        {table.sortable && <ProductSort table={table} />}
                        {!picker && <SelectPage table={table} />}
                    </div>
                }
                renderItem={(product: any) => <ProductCardGroup table={table} product={product} picker={picker} />}
                empty={empty}
                pagination={{
                    current: table.page,
                    pageSize: table.limit,
                    total: table.total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        const pageChanged = page !== table.page
                        table.setTableState(page, pageSize, table.sort)
                        if (pageChanged) scrollToTop()
                    },
                }}
            />
        </section>
    )

    const { sortable } = table
    const sortOrder = (key: ProductSortKey): SortOrder => {
        if (!table.sort || table.sort.key !== key) return null
        return table.sort.order === 'asc' ? 'ascend' : 'descend'
    }

    const columns: ColumnsType<any> = [
        {
            key: 'name',
            title: i.t('productCards:col.product'),
            sorter: sortable,
            sortOrder: sortable ? sortOrder('name') : undefined,
            render: (product: any) => product.__skeleton
                ? <div className="pt-product">
                    <Skeleton.Button active size="small" shape="round" className="pt-skeleton-toggle" />
                    <Skeleton.Avatar active shape="square" size={52} />
                    <Skeleton active title={false} paragraph={{ rows: 2, width: ['70%', '40%'] }} />
                </div>
                : <ProductCell table={table} product={product} picker={picker} />,
        },
        {
            key: 'price',
            title: i.t('productCards:col.price'),
            align: 'right',
            width: picker ? 136 : 160,
            sorter: sortable,
            sortOrder: sortable ? sortOrder('price') : undefined,
            render: (product: any) => skeleton(<PriceCell product={product} />)(product),
        },
        {
            key: 'count',
            title: i.t('productCards:col.stock'),
            align: 'right',
            width: picker ? 128 : 150,
            sorter: sortable,
            sortOrder: sortable ? sortOrder('count') : undefined,
            render: (product: any) => skeleton(<StockCell product={product} />)(product),
        },
        // В модалке выбора рейтинг не нужен — место отдаём кнопке «Выбрать»
        ...(picker ? [] : [{
            key: 'rating',
            title: i.t('productCards:col.rating'),
            // По содержимому: «★ 4.8 126» в одну строку
            width: 104,
            className: 'pt-col-rating',
            render: (product: any) => skeleton(<RatingCell product={product} />)(product),
        }]),
        picker
            ? {
                key: 'actions',
                // «Добавить» шире «Выбрать»
                width: picker.add ? 184 : 168,
                align: 'right',
                // В узкой форме таблица прокручивается вбок — «Выбрать» остаётся на виду
                fixed: 'right',
                render: (product: any) => !product.__skeleton && <PickActions product={product} picker={picker} />,
            }
            : {
                key: 'actions',
                width: table.canMove ? 148 : 52,
                align: 'right',
                render: (product: any) => !product.__skeleton &&
                    <span className="pt-actions">
                        {table.canMove && <MoveButtons table={table} product={product} />}
                        <Tooltip title={i.t('productCards:open')}>
                            <Button className="pt-open" type="text" shape="circle" icon={<RightOutlined />} href={productUrl(product.id)} />
                        </Tooltip>
                    </span>,
            },
    ]

    return (
        <section className={`pt${picker ? ' pt--picker' : ''}`} ref={sectionRef}>
            <div className="pt__top">
                <ProductToolbar table={table} />
                {!picker && <BulkBar table={table} />}
            </div>

            <Table
                className="pt__table"
                rowKey="id"
                columns={columns}
                dataSource={firstLoad ? SKELETON_ROWS : list}
                loading={{ spinning: loading && !firstLoad, delay: 150 }}
                scroll={{ x: picker ? 700 : 900 }}
                showSorterTooltip={false}
                sortDirections={['ascend', 'descend']}
                rowClassName={(product: any, _index, indent) => [
                    'pt-row',
                    indent > 0 && 'pt-row--child',
                    !product.__skeleton && !(Number(product.view) > 0) && 'pt-row--hidden',
                    picker && picker.selectedIds.includes(Number(product.id)) && 'pt-row--picked',
                    picker?.add && picker.selectedIds.includes(Number(product.id)) && 'pt-row--done',
                ].filter(Boolean).join(' ')}
                // Вариации (children) — вложенные строки; раскрываются меткой в строке товара, без значка antd
                expandable={{
                    expandedRowKeys: table.expandedIds,
                    expandIcon: () => null,
                    indentSize: 0,
                }}
                onRow={(product: any) => ({ onClick: onRowClick(table, product, picker) })}
                rowSelection={picker ? undefined : {
                    selectedRowKeys: table.selectedIds,
                    onChange: keys => table.setSelectedIds(keys as number[]),
                    getCheckboxProps: (product: any) => ({ disabled: !!product.__skeleton }),
                }}
                locale={{ emptyText: loading ? ' ' : <ListEmpty {...empty} /> }}
                pagination={{
                    current: table.page,
                    pageSize: table.limit,
                    total: table.total,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, [from, to]) => i.t('productCards:range', { from, to, total: total.toLocaleString() }),
                    position: ['bottomRight'],
                }}
                onChange={(pagination, _filters, sorter: any) => {
                    const order = Array.isArray(sorter) ? null : sorter?.order
                    const key = Array.isArray(sorter) ? null : sorter?.columnKey
                    const pageChanged = (pagination.current ?? 1) !== table.page

                    table.setTableState(
                        pagination.current ?? 1,
                        pagination.pageSize ?? table.limit,
                        order && key ? { key, order: order === 'ascend' ? 'asc' : 'desc' } : null,
                    )

                    if (pageChanged) scrollToTop()
                }}
            />
        </section>
    )
})
